import axios, { type AxiosError, type AxiosInstance } from 'axios';

import { API_BASE_URL } from '@shared/config/api';

import { mapRequestConfig, toHttpResponse } from './lib/http-client-mappers';

export interface CreateHttpClientConfig {
	baseURL?: string;
	withCredentials?: boolean;
}

class HttpClientAdapter implements HttpClient {
	private instance: AxiosInstance;

	constructor({ baseURL, withCredentials }: CreateHttpClientConfig) {
		this.instance = axios.create({
			baseURL,
			withCredentials,
		});
		this.initResponseInterceptor();
	}

	public async get<T = unknown>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
		const response = await this.instance.get<T>(url, mapRequestConfig(config));
		return toHttpResponse(response);
	}

	public async post<T = unknown>(
		url: string,
		data?: unknown,
		config?: HttpRequestConfig
	): Promise<HttpResponse<T>> {
		const response = await this.instance.post<T>(url, data, mapRequestConfig(config));
		return toHttpResponse(response);
	}

	public async put<T = unknown>(
		url: string,
		data?: unknown,
		config?: HttpRequestConfig
	): Promise<HttpResponse<T>> {
		const response = await this.instance.put<T>(url, data, mapRequestConfig(config));
		return toHttpResponse(response);
	}

	public async patch<T = unknown>(
		url: string,
		data?: unknown,
		config?: HttpRequestConfig
	): Promise<HttpResponse<T>> {
		const response = await this.instance.patch<T>(url, data, mapRequestConfig(config));
		return toHttpResponse(response);
	}

	public async delete<T = unknown>(
		url: string,
		config?: HttpRequestConfig
	): Promise<HttpResponse<T>> {
		const response = await this.instance.delete<T>(url, mapRequestConfig(config));
		return toHttpResponse(response);
	}

	private initResponseInterceptor() {
		this.instance.interceptors.response.use(
			(response) => response,
			(error: AxiosError) => {
				// Для cookie-auth ручек считаем 401 ожидаемым исходом:
				// возвращаем ответ дальше по цепочке, чтобы бизнес-слой обработал его как isOkStatus=false.
				if (error.response?.status === 401) {
					return Promise.resolve(error.response);
				}

				return Promise.reject(error);
			}
		);
	}
}

export interface HttpClient {
	delete<T = unknown>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>>;
	get<T = unknown>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>>;
	patch<T = unknown>(
		url: string,
		data?: unknown,
		config?: HttpRequestConfig
	): Promise<HttpResponse<T>>;
	post<T = unknown>(
		url: string,
		data?: unknown,
		config?: HttpRequestConfig
	): Promise<HttpResponse<T>>;
	put<T = unknown>(
		url: string,
		data?: unknown,
		config?: HttpRequestConfig
	): Promise<HttpResponse<T>>;
}

export const httpClient = new HttpClientAdapter({
	baseURL: API_BASE_URL,
	withCredentials: true,
});

export interface HttpRequestConfig {
	headers?: Record<string, string>;
	params?: Record<string, unknown>;
	signal?: AbortSignal;
}

export interface HttpResponse<T = unknown> {
	data: T;
	headers: unknown;
	status: number;
}
